import {html, type HTMLTemplateResult, type PropertyValues} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {get, translate} from "lit-translate";

import {AbstractO2dElement} from "@o2d/shared";
import {
    elapsed,
    formatTime,
    INITIAL_STOPWATCH,
    isRunning,
    parts,
    pause,
    reset,
    start,
    type StopwatchState,
} from "../../../domain/gimmicks/stopwatch.state";
import {loadState, saveState, subscribe} from "../../../domain/gimmicks/stopwatch.store";
import {FrameLoopController} from "../frame-loop.controller";
import {GimmickSkinController} from "../gimmick-skin.controller";
import {CLOCK_SKIN_RENDERERS} from "./skins/clock-skins";
import type {SkinInput} from "./skins/skin-input";

/**
 * The stopwatch gimmick (feature plan 0071), mounted by `link-tile-gimmick` inside a Linkboard tile.
 *
 * - STATE per viewer and tile, in browser storage (`stopwatch.store.ts`): it runs for everybody
 *   separately and keeps running across a reload or a remount of the tile (after a save or a lock change).
 *   Another tab of the same browser follows through the `storage` event.
 * - LOOK: `savedSkin` is the look stored with the tile (dialog); the "Optik wechseln" button switches it for
 *   this viewer only. Their choice lapses once someone changes the saved look. All of that is the
 *   `GimmickSkinController`, shared with the picker (feature plan 0072).
 * - FRAMES: the `FrameLoopController` (shared with the timer, feature plan 0073) runs a loop on the base
 *   class's `requestFrame` only while the stopwatch runs, the element is connected and the page is visible —
 *   a paused or hidden stopwatch costs at most one frame.
 * - A11Y: `role="timer"` without `aria-live` (it would announce every hundredth); the readable time sits in
 *   the `aria-label`, renewed on start, pause and reset, and on every reload of the state — mounting, a new
 *   tile id, another tab's write of the state. A new look (button, dialog, another tab) does not renew it.
 *   Never per frame.
 *
 * A NEW component, so it extends `AbstractO2dElement` (ADR-007; Light DOM); all styles in ralphy.css,
 * sections "Gimmicks: gemeinsamer Rahmen" (`.gimmick*`) and "Gimmicks: Stoppuhr". Its own listeners are removed in `disconnectedCallback` — no `_disconnected$`
 * here, and the element may be connected again (a tile is remounted after a save).
 */
@customElement('o2d-gimmick-stopwatch')
export class GimmickStopwatchComponent extends AbstractO2dElement {

    /** The tile's id — the key of this viewer's state. */
    @property({type: String})
    public tileId = '';

    /** The look stored with the tile, raw (may be one this version does not know). */
    @property({type: String})
    public savedSkin = '';

    @state()
    private _state: StopwatchState = INITIAL_STOPWATCH;

    /** "Now" of the last frame — only moves while running. */
    @state()
    private _now = Date.now();

    /** The time the `aria-label` reads — renewed by `_announce`, never per frame. */
    @state()
    private _announced = formatTime(parts(0));

    private _unsubscribe: (() => void) | null = null;
    private readonly _skinController = new GimmickSkinController(this, 'stopwatch');
    private readonly _frameLoop = new FrameLoopController(this, {
        running: () => isRunning(this._state),
        onFrame: () => {
            this._now = Date.now();
        },
        schedule: (callback) => this.requestFrame(callback),
    });

    public override connectedCallback(): void {
        super.connectedCallback();
        this._listen();
        this._reload();
    }

    public override disconnectedCallback(): void {
        this._unsubscribe?.();
        this._unsubscribe = null;
        super.disconnectedCallback(); // the frame loop stops; the base cancels the pending frame
    }

    protected override willUpdate(changed: PropertyValues): void {
        // The first update follows `connectedCallback`, which has already listened and loaded with the values
        // Lit set before inserting the element — only a later tile id needs a reload. The look (`savedSkin`)
        // is the skin controller's.
        if (this.hasUpdated && this.isConnected && changed.has('tileId')) {
            this._listen();
            this._reload();
        }
    }

    protected render(): HTMLTemplateResult {
        const running = isRunning(this._state);
        const ms = elapsed(this._state, this._now);
        const skin = this._skinController.skin;
        const input: SkinInput = {ms, parts: parts(ms), running};

        return html`
            <div class="gimmick stopwatch" data-skin=${skin} data-running=${running ? 'true' : 'false'}
                 role="timer" aria-label=${get('gimmicks.stopwatch.timer-label', {time: this._announced})}>
                <div class="gimmick__face">${CLOCK_SKIN_RENDERERS[skin](input)}</div>
                <div class="gimmick__controls">
                    <button type="button"
                            class=${running ? 'gimmick__toggle gimmick__toggle--pause' : 'gimmick__toggle gimmick__toggle--start'}
                            aria-label=${translate(running ? 'gimmicks.pause' : 'gimmicks.start')}
                            title=${translate(running ? 'gimmicks.pause' : 'gimmicks.start')}
                            @click=${this._onToggle}></button>
                    <button type="button" class="gimmick__reset"
                            aria-label=${translate('gimmicks.reset')}
                            title=${translate('gimmicks.reset')}
                            @click=${this._onReset}></button>
                    <button type="button" class="gimmick__next-skin"
                            aria-label=${translate('gimmicks.next-skin')}
                            title=${translate('gimmicks.next-skin')}
                            @click=${this._onNextSkin}></button>
                </div>
            </div>
        `;
    }

    private readonly _onToggle = (): void => {
        const now = Date.now();
        this._apply(isRunning(this._state) ? pause(this._state, now) : start(this._state, now), now);
    };

    private readonly _onReset = (): void => {
        this._apply(reset(), Date.now());
    };

    private readonly _onNextSkin = (): void => {
        this._skinController.next();
    };

    private _apply(next: StopwatchState, now: number): void {
        this._state = next;
        this._announce(now);
        saveState(this.tileId, next);
        this._frameLoop.sync();
    }

    /** Moves "now" and renews the time the `aria-label` reads — on start, pause, reset and every reload
     *  (mounting, a new tile id, another tab's write of the state). */
    private _announce(now: number): void {
        this._now = now;
        this._announced = formatTime(parts(elapsed(this._state, now)));
    }

    /** Reads the state from storage — at connect, on a new tile id, and when another tab wrote it. */
    private _reload(): void {
        const now = Date.now();
        this._state = loadState(this.tileId, now);
        this._announce(now);
        this._frameLoop.sync();
    }

    private _listen(): void {
        this._unsubscribe?.();
        this._unsubscribe = this.tileId ? subscribe(this.tileId, () => this._reload()) : null;
    }
}
